import * as _ from 'lodash';

import { Map } from "immutable";

import {
    DEFAULT_COLOR,
    DEFAULT_NOTE_TYPE,
    DEFAULT_PROCESS_STEP,
    NoteColor,
    NoteType,
    ProcessStep,
} from '../constants';

import { Note, Presentation, ShapeType, SlideData } from '../reducers';
import { BasicData } from "../reducers/presentation";
import { PresentationOverview } from "./presentation-store";
import {
    createEllipsePath,
    createEllipseRandom,
    createLinePath,
    createLineRandom,
    createRectPath,
    createRectRandom
} from "./path-data";
import {
    ArrowShape,
    BaseShape,
    BoundsType,
    convertPointsToPath,
    createArrowPath,
    EllipseShape,
    FreeformShape,
    LineShape,
    Point,
    RectShape,
    ShapeData,
    TextShape
} from "./shape-service";
import { fitTextBox } from "./text-service";
import { createSlidePathData, wrapNotes } from "./slide_helper";


function decodeProcessStep(data: any) {
    switch (_.toString(data)) {
        case 'BRAINSTORM':
            return ProcessStep.BRAINSTORM;
        case 'BASIC_DATA':
            return ProcessStep.BASIC_DATA;
        case 'STRUCTURE':
            return ProcessStep.STRUCTURE;
        case 'SKETCH':
            return ProcessStep.SKETCH;
        case 'EXPORT':
            return ProcessStep.EXPORT;
        default:
            return DEFAULT_PROCESS_STEP;
    }
}

function decodeBasicData(data: any): BasicData {
    return {
        goals: _.toString(data.goals),
        mainMessage: _.toString(data.mainMessage),
        audience: _.toString(data.audience),
    }
}

function decodeNoteColor(data: any) {
    switch (_.toString(data)) {
        case 'YELLOW':
            return NoteColor.YELLOW;
        case 'PINK':
            return NoteColor.PINK;
        case 'GREEN':
            return NoteColor.GREEN;
        case 'ORANGE':
            return NoteColor.ORANGE;
        default:
            return DEFAULT_COLOR;
    }
}

function decodeNoteType(data: any) {
    switch (_.toString(data)) {
        case NoteType.T0:
            return NoteType.T0;
        case NoteType.T1:
            return NoteType.T1;
        case NoteType.T2:
            return NoteType.T2;
        case NoteType.T3:
            return NoteType.T3;
        default:
            return DEFAULT_NOTE_TYPE;
    }
}

function decodeNote(data: any): Note {
    return {
        uuid: _.toString(data.uuid),
        x: _.toNumber(data.x),
        y: _.toNumber(data.y),
        text: _.toString(data.text),
        color: decodeNoteColor(data.color),
        type: decodeNoteType(data.type),
    };
}

function decodeNotes(data: any[]): ReadonlyArray<Note> {
    return data
        .filter(note => _.isObject(note))
        .map(note => decodeNote(note));
}

function decodeShapeType(data: any): ShapeType {
    const s = _.toString(data);
    switch(s) {
        case 'ARROW':
        case 'RECT':
        case 'ELLIPSE':
        case 'FREEFORM':
        case 'TEXT':
            return s;
        default:
            return 'LINE';
    }
}

function decodeLine(data: any, baseShape: BaseShape): LineShape {
    const random = createLineRandom();
    const path = createLinePath(0, 0, baseShape.x2 - baseShape.x1, baseShape.y2 - baseShape.y1, random);
    return {
        ...baseShape,
        type: 'LINE',
        path,
        random,
    }
}

function decodeArrow(data: any, baseShape: BaseShape): ArrowShape {
    const mainRandom = createLineRandom();
    const arm1Random = createLineRandom();
    const arm2Random = createLineRandom();
    const path = createArrowPath(0, 0, baseShape.x2 - baseShape.x1, baseShape.y2 - baseShape.y1, mainRandom, arm1Random, arm2Random);
    return {
        ...baseShape,
        type: 'ARROW',
        path,
        mainRandom,
        arm1Random,
        arm2Random,
    }
}

function decodeRect(data: any, baseShape: BaseShape): RectShape {
    const random = createRectRandom();
    const path = createRectPath(0, 0, baseShape.x2 - baseShape.x1, baseShape.y2 - baseShape.y1, random);
    return {
        ...baseShape,
        type: 'RECT',
        path,
        random,
    }
}

function decodeEllipse(data: any, baseShape: BaseShape): EllipseShape {
    const random = createEllipseRandom();
    const path = createEllipsePath(0, 0, baseShape.x2 - baseShape.x1, baseShape.y2 - baseShape.y1, random);
    return {
        ...baseShape,
        type: 'ELLIPSE',
        path,
        random,
    }
}

function decodeFreeform(data: any, baseShape: BaseShape): FreeformShape {
    const coordsX = _.isArray(data.coordsX)? data.coordsX : [];
    const coordsY = _.isArray(data.coordsY)? data.coordsY : [];
    const points: ReadonlyArray<Point> = _.zip(coordsX, coordsY)
        .map(coords => ({ x: _.toNumber(coords[0]), y: _.toNumber(coords[1]) }));
    const path = convertPointsToPath(points);
    return {
        ...baseShape,
        type: 'FREEFORM',
        path,
        points,
    }
}

function decodeBoundsType(data: any): BoundsType {
    switch (_.toString(data)) {
        case 'DYNAMIC':
            return BoundsType.DYNAMIC;
        default:
            return BoundsType.FIXED;
    }
}

function decodeText(data: any, baseShape: BaseShape): TextShape {
    const text = _.toString(data.text);
    const width = Math.abs(baseShape.x2 - baseShape.x1);
    const height = Math.abs(baseShape.y2 - baseShape.y1);
    const wrappedText = fitTextBox(text, width, height);
    return {
        ...baseShape,
        type: 'TEXT',
        path: '',
        text,
        lines: wrappedText.lines,
        size: wrappedText.size,
        boundsType: decodeBoundsType(data.boundsType),
        rotation: 10 * Math.random() - 5,
    }
}

function decodeShape(data: any): ShapeData {
    const baseShape: BaseShape = {
        type: decodeShapeType(data.type),
        uuid: _.toString(data.uuid),
        x1: _.toNumber(data.x1),
        y1: _.toNumber(data.y1),
        x2: _.toNumber(data.x2),
        y2: _.toNumber(data.y2),
        path: '',
    };
    switch(baseShape.type) {
        case 'ARROW':
            return decodeArrow(data, baseShape);
        case 'RECT':
            return decodeRect(data, baseShape);
        case 'ELLIPSE':
            return decodeEllipse(data, baseShape);
        case 'FREEFORM':
            return decodeFreeform(data, baseShape);
        case 'TEXT':
            return decodeText(data, baseShape);
        default:
            return decodeLine(data, baseShape);
    }
}

function decodeShapes(data: any[]): ReadonlyArray<ShapeData> {
    return data
        .filter(shape => _.isObject(shape))
        .map(shape => decodeShape(shape));
}

function decodeSlide(data: any): SlideData {
    const notes = _.toString(data.notes);
    return {
        uuid: _.toString(data.uuid),
        x: _.toNumber(data.x),
        y: _.toNumber(data.y),
        notes,
        notesLines: wrapNotes(notes),
        pathData: createSlidePathData(),
        shapes: _.isArray(data.shapes)? decodeShapes(data.shapes) : [],
    };
}

function decodeSlides(data: any[]): ReadonlyArray<SlideData> {
    return data
        .filter(slide => _.isObject(slide))
        .map(slide => decodeSlide(slide));
}

export function decodePresentation(data: any): Presentation {
    return {
        id: _.toNumber(data.id),
        processStep: decodeProcessStep(data.processStep),
        basicData: decodeBasicData(_.isObject(data.basicData) ? data.basicData : {}),
        templateId: _.toNumber(data.templateId),
        notes: _.isArray(data.notes)? decodeNotes(data.notes) : [],
        slides: _.isArray(data.slides)? decodeSlides(data.slides) : [],
        textFields: Map(data.textFields),
    };
}

function decodePresentationOverview(data: any): PresentationOverview {
    return {
        id: _.toNumber(data.id),
        name: _.toString(data.name),
        created: new Date(),
        updated: new Date(),
    }
}

export function decodePresentationOverviews(data: any): ReadonlyArray<PresentationOverview> {
    if (! _.isArray(data)) {
        return [];
    }
    return data
        .filter(note => _.isObject(note))
        .map(note => decodePresentationOverview(note));
}



// WEBPACK FOOTER //
// ./src/services/encoder.ts